import { useEffect, useMemo, useState } from "react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "under_review", "contacted", "offer_made", "purchased", "rejected"];

export default function SubmissionsPanel({ onExport }) {
  const { t } = useLang();
  const s = t("sell");
  const [subs, setSubs] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");

  const load = () => {
    fetch(`${API}/vehicle-submissions`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setSubs)
      .catch(() => setSubs([]));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!subs) return [];
    const needle = q.trim().toLowerCase();
    return subs.filter((x) => {
      if (statusFilter !== "all" && (x.status || "new") !== statusFilter) return false;
      if (!needle) return true;
      return [x.full_name, x.email, x.make, x.model, x.country, x.phone]
        .some((v) => String(v || "").toLowerCase().includes(needle));
    });
  }, [subs, q, statusFilter]);

  const patch = async (id, body) => {
    const res = await fetch(`${API}/vehicle-submissions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const updated = await res.json();
      setSubs((list) => list.map((x) => (x.id === id ? updated : x)));
    }
  };

  const addNote = async (id) => {
    if (!noteDraft.trim()) return;
    await patch(id, { note: noteDraft });
    setNoteDraft("");
  };

  const vehicleOf = (x) => `${x.make} ${x.model === "other" ? x.model_other || s.admin.other : x.model} — ${x.year}`;

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <input
          className="field-input md:max-w-xs"
          placeholder={s.admin.searchPh}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="submissions-search-input"
        />
        <select
          className="field-input md:max-w-[220px] cursor-pointer"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="submissions-status-filter"
        >
          <option value="all">{s.admin.filterAll}</option>
          {STATUSES.map((st) => <option key={st} value={st}>{s.admin.statuses[st]}</option>)}
        </select>
        <span className="mono text-[11px] text-[#666663] uppercase tracking-[0.14em]" data-testid="submissions-count">
          {subs ? `${filtered.length} / ${subs.length}` : "…"}
        </span>
        {filtered.length > 0 && onExport && (
          <button
            onClick={() => onExport(`veyrone-vehicle-submissions-${new Date().toISOString().slice(0, 10)}.csv`, filtered)}
            className="btn !py-2 !px-4 md:ml-auto"
            data-testid="submissions-export-btn"
          >
            {s.admin.exportCsv}
          </button>
        )}
      </div>

      {subs === null && <p className="mono text-[12px] text-[#666663]">…</p>}
      {subs && subs.length === 0 && <p className="mono text-[12px] text-[#666663]" data-testid="submissions-empty">{s.admin.empty}</p>}

      {subs && filtered.length > 0 && (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-left" data-testid="admin-submissions-table">
            <thead>
              <tr className="border-b border-white/10">
                {[s.admin.colDate, s.admin.colSeller, s.admin.colVehicle, s.admin.colPrice, s.admin.colCountry, s.admin.colStatus].map((h) => (
                  <th key={h} className="px-4 py-3"><span className="field-label !text-[9px] !mb-0">{h}</span></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((x) => (
                <tr key={x.id} onClick={() => setOpenId(x.id)} className="border-b border-white/[0.06] align-top hover:bg-white/[0.02] transition-colors cursor-pointer" data-testid="admin-submission-row">
                  <td className="px-4 py-3 mono text-[11px] text-[#999995] whitespace-nowrap">{(x.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-[13px] text-[#F3F3F0]">{x.full_name}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#999995] whitespace-nowrap">{vehicleOf(x)}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#D1C7BD] whitespace-nowrap">{Number(x.asking_price).toLocaleString()} {x.currency}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#999995]">{x.country}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      className="field-input !py-1.5 !text-[11px] cursor-pointer min-w-[140px]"
                      value={x.status || "new"}
                      onChange={(e) => patch(x.id, { status: e.target.value })}
                      data-testid={`submission-status-${x.id}`}
                    >
                      {STATUSES.map((st) => <option key={st} value={st}>{s.admin.statuses[st]}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail views for opened submissions */}
      {subs && subs.filter((x) => x.id === openId).map((x) => (
        <div key={x.id} className="fixed inset-0 z-50 bg-[#050607]/80 flex items-start justify-center overflow-y-auto p-4 md:p-10" onClick={() => setOpenId(null)} data-testid="submission-detail-overlay">
          <div className="w-full max-w-3xl bg-[#090A0B] border border-white/15 p-6 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="submission-detail">
            <div className="flex items-center justify-between mb-8">
              <div className="wordmark text-[13px] text-[#F3F3F0]">{s.admin.detailTitle}</div>
              <button onClick={() => { setOpenId(null); setNoteDraft(""); }} className="btn !py-2 !px-4" data-testid="submission-detail-close">{s.admin.close}</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mb-10">
              {[
                [s.admin.fSeller, x.full_name], [s.admin.fEmail, x.email], [s.admin.fPhone, x.phone],
                [s.admin.fContactMethod, s.contactOpts[x.preferred_contact] || x.preferred_contact],
                [s.admin.fVehicle, vehicleOf(x)], [s.admin.fMileage, `${Number(x.mileage_km).toLocaleString()} km`],
                [s.admin.fFuel, s.fuelOpts[x.fuel_type] || x.fuel_type],
                [s.admin.fTransmission, s.transOpts[x.transmission] || x.transmission],
                [s.admin.fCondition, s.condOpts[x.condition] || x.condition],
                [s.admin.fCountry, x.country], [s.admin.fRegistration, x.registration_spec || "—"],
                [s.admin.fPrice, `${Number(x.asking_price).toLocaleString()} ${x.currency}`],
                [s.admin.fDate, (x.created_at || "").slice(0, 16).replace("T", " ")],
                [s.admin.fId, x.id],
                [s.admin.fNotes, x.notes || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex flex-col">
                  <span className="field-label !text-[9px] !mb-1">{k}</span>
                  <span className="text-[13px] text-[#F3F3F0] break-words">{v}</span>
                </div>
              ))}
            </div>

            <div className="field-label mb-3">{s.admin.photosTitle} ({(x.photo_ids || []).length})</div>
            {(x.photo_ids || []).length === 0 && <p className="mono text-[11px] text-[#666663] mb-8">{s.admin.noPhotos}</p>}
            {(x.photo_ids || []).length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">
                {x.photo_ids.map((pid) => (
                  <a key={pid} href={`${API}/vehicle-submissions/${x.id}/photos/${pid}`} target="_blank" rel="noopener noreferrer" data-testid="submission-photo">
                    <img src={`${API}/vehicle-submissions/${x.id}/photos/${pid}`} alt={`${vehicleOf(x)} — ${s.admin.photosTitle}`}
                      className="w-full h-28 object-cover border border-white/10 hover:border-white/40 transition-colors duration-300" />
                  </a>
                ))}
              </div>
            )}

            <div className="field-label mb-3">{s.admin.notesTitle}</div>
            {(x.internal_notes || []).length === 0 && <p className="mono text-[11px] text-[#666663] mb-4">{s.admin.noNotes}</p>}
            <div className="space-y-2 mb-4">
              {(x.internal_notes || []).map((n, i) => (
                <div key={i} className="border border-white/10 p-3" data-testid="submission-note">
                  <div className="mono text-[9px] text-[#555654] uppercase tracking-[0.14em] mb-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div>
                  <p className="text-[12px] text-[#999995]">{n.text}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              <input className="field-input flex-1" placeholder={s.admin.notePh} value={noteDraft} maxLength={1000}
                onChange={(e) => setNoteDraft(e.target.value)} data-testid="submission-note-input" />
              <button onClick={() => addNote(x.id)} className="btn !py-2 !px-5" data-testid="submission-note-add">{s.admin.addNote}</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
